<script setup lang="ts">
import { logger } from '@pkg/logger';
import { Autocomplete } from '@pkg//ui/components/ui/autocomplete';
import type { Image } from '@sanity/types';
import { getLanguageCodeFromLocale } from '@pkg/utilities/i18n';
import type { BagsApiResponse, Origin, SurpriseBag, SurpriseBagTranslations, BagNotFoundCopy } from '@pkg/types/surpriseBag';
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import { getBagAvailability } from '@pkg/utilities/surprisebag';
import { BAG_AVAILABILITY } from '@pkg/types/surpriseBag';
import { ampli } from '@pkg/analytics-helper/src/ampli';
import SurpriseBagResultsList from './SurpriseBagResultsList.vue';
import { useSearchParams } from '~/app/composables/useSearchParams';
import { useFindSearchContext } from '~/app/composables/useFindSearchContext';
import { useLocationAutocomplete } from '~/app/composables/useLocationAutocomplete';

const props = withDefaults(defineProps<{
  preTitle: string;
  title: string;
  placeholder: string;
  image: Image;
  is404?: boolean;
  resultsTitle: string;
  noLocationText: string;
  useMyLocationLabel: string;
  dontUseMyLocationLabel: string;
  loadingMyLocationLabel: string;
  geolocationErrorLabel: string;
  bagNotFoundCopy: BagNotFoundCopy;
  myLocationLabel: string;
  ctaText: string;
  availableBagsFilterLabel?: string;
  emptyStateText?: string;
  errorStateText?: string;
  staticTranslations: SurpriseBagTranslations;
}>(), {
  is404: false,
  availableBagsFilterLabel: '',
  emptyStateText: '',
  errorStateText: '',
});

type OriginWithPlaceId = Origin & { place_id?: string };

enum GeolocationState {
  ACTIVE = 'active',
  LOADING = 'waiting',
  DEFAULT = 'inactive',
  PERMISSION_DENIED = 'permission_denied',
  ERROR = 'error',
}

enum FilterType {
  ALL = 'no_filter',
  SHOW_AVAILABLE_ITEMS = 'show_available_items',
}

const { suggestions, loadingSuggestions, fetchSuggestions } = useLocationAutocomplete();
const showList = ref(false);
const emptyResults = ref(false);
const errorState = ref(false);
const { locale, t } = useI18n();
const { searchParams, getLocationFromUrl, saveLocationToUrl, clearSearchContextFromUrl } = useSearchParams();
const { getAndClearFindSearchContext } = useFindSearchContext();
const locationToSearch = ref<OriginWithPlaceId | null>(null);
const surpriseBags = ref<SurpriseBag[]>([]);
const pageToSearch = ref<number>(0);
const disabledButton = ref(false);
const isFilterLoading = ref(false);
const tempBags = ref<SurpriseBag[]>([]);
const currentFetchSeq = ref(0);
const coordinates = ref<{ latitude: number; longitude: number; } | null>(null);
const filterBagAvailability = ref<FilterType>(FilterType.ALL);

// Initial search value from URL params for Autocomplete
const initialSearchValue = computed(() => searchParams.value.search);

const displayBags = computed(() => {
  return isFilterLoading.value && tempBags.value.length > 0 ? tempBags.value : surpriseBags.value;
});

const geolocationLabel = computed(() => {
  switch (myGeolocation.value) {
    case GeolocationState.LOADING:
      return props.loadingMyLocationLabel;
    case GeolocationState.PERMISSION_DENIED:
      return t('locationPermissionDeniedLabel');
    case GeolocationState.ERROR:
      return props.geolocationErrorLabel;
    case GeolocationState.ACTIVE:
      return props.dontUseMyLocationLabel;
    case GeolocationState.DEFAULT:
    default:
      return props.useMyLocationLabel;
  }
});

const isGeolocationPermissionDenied = computed(() => myGeolocation.value === GeolocationState.PERMISSION_DENIED);

const myGeolocation = ref<GeolocationState>(GeolocationState.DEFAULT);

const objectToSearch = computed(() => ({
  displayName: props.myLocationLabel,
  center: {
    latitude: coordinates.value?.latitude || 0,
    longitude: coordinates.value?.longitude || 0,
  },
}));

const getGeolocation = (event: Event) => {
  event.preventDefault();

  if (myGeolocation.value === GeolocationState.PERMISSION_DENIED) {
    return;
  }

  const currentLocationState = myGeolocation.value;
  myGeolocation.value = GeolocationState.LOADING;

  const maxWaitTime = 25000;

  if (currentLocationState === GeolocationState.DEFAULT) {
    if (!navigator || !navigator.geolocation) {
      myGeolocation.value = GeolocationState.ERROR;
      return;
    }

    let didRespond = false;

    const fallbackTimeout = setTimeout(() => {
      if (!didRespond) {
        logger.warn('[LocationFinder]: Geolocation request timed out.');
        myGeolocation.value = GeolocationState.ERROR;
      }
    }, maxWaitTime);

    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          didRespond = true;
          clearTimeout(fallbackTimeout);
          coordinates.value = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          };
          myGeolocation.value = GeolocationState.ACTIVE;
        },
        (err) => {
          didRespond = true;
          clearTimeout(fallbackTimeout);
          logger.error('[LocationFinder]:', err);
          myGeolocation.value = err.code === GeolocationPositionError.PERMISSION_DENIED
            ? GeolocationState.PERMISSION_DENIED
            : GeolocationState.ERROR;
        },
        {
          enableHighAccuracy: false,
          timeout: maxWaitTime,
          maximumAge: 0,
        },
      );
    } catch (error) {
      clearTimeout(fallbackTimeout);
      logger.error('[LocationFinder] timeout error:', error);
      myGeolocation.value = GeolocationState.ERROR;
    }
  } else if (currentLocationState === GeolocationState.ACTIVE) {
    resetSearch();
  }
};

const resetSearch = () => {
  locationToSearch.value = null;
  surpriseBags.value = [];
  pageToSearch.value = 0;
  disabledButton.value = false;
  showList.value = false;
  emptyResults.value = false;
  errorState.value = false;
  coordinates.value = null;
  myGeolocation.value = GeolocationState.DEFAULT;
};

type FetchOptions = { size?: number; radiusKm?: number; replace?: boolean; applySeq?: number };
const fetchSurpriseBags = async(origin?: OriginWithPlaceId, options: FetchOptions = {}) => {
  const locationData = origin || locationToSearch.value;

  if (origin) {
    surpriseBags.value = [];
  }

  try {
    if (!locationData?.center && locationData?.place_id) {
      const placeId = locationData.place_id;

      try {
        const placeDetails = await $fetch('/api/location-coordinates-google', {
          params: { placeId },
        }) as any;

        if (placeDetails.location) {
          locationData.center = placeDetails.location;
          logger.info('Updated coordinates from Google Places API:', locationData.center);
        }
      } catch (error) {
        logger.error('[LocationFinder] Google Places API error:', error);
      }
    }

    const response = await $fetch<BagsApiResponse>('/api/surprise-bags', {
      method: 'GET',
      params: {
        lat: locationData?.center?.latitude,
        lng: locationData?.center?.longitude,
        language: getLanguageCodeFromLocale(locale.value as LOCALE_CODE),
        locale: locale.value,
        page: pageToSearch.value,
        withStockOnly: filterBagAvailability.value === FilterType.SHOW_AVAILABLE_ITEMS ? '1' : '0',
        size: options.size,
        radius: typeof options.radiusKm === 'number' ? Math.max(1, Math.round(options.radiusKm)) : undefined,
      },
    });

    if (response.success) {
      if (typeof options.applySeq === 'number' && options.applySeq !== currentFetchSeq.value) {
        return; // ignore outdated response
      }
      disabledButton.value = !response.canLoadMore;
      locationToSearch.value = locationData;
      locationData && saveLocationToUrl(locationData);

      if (origin || options.replace) {
        surpriseBags.value = response.payload.items as SurpriseBag[];
      } else {
        surpriseBags.value = surpriseBags?.value.concat(response.payload.items as SurpriseBag[]);
      }
      showList.value = !!(surpriseBags.value && surpriseBags.value.length > 0);
      emptyResults.value = surpriseBags.value.length === 0;
      analyticsMeasureAvailableItems(surpriseBags, locationData || undefined);
    }
  } catch (error) {
    logger.error('[LocationFinder] fetch error:', error);
    showList.value = false;
    errorState.value = true;
  }
};

const analyticsMeasureAvailableItems = (surpriseBags: Ref<SurpriseBag[]>, locationData?: OriginWithPlaceId) => {
  let availableCount = 0;
  surpriseBags.value.forEach((bag: SurpriseBag) => {
    if (getBagAvailability(bag) === BAG_AVAILABILITY.AVAILABLE) {
      availableCount++;
    }
  });
  try {
    if (locationData?.displayName) {
      ampli.findItems({
        available_item_ct: availableCount.toString(),
        item_displayed_ct: surpriseBags.value.length.toString(),
      });
      const searchContext = getAndClearFindSearchContext() ?? searchParams.value.search_context?.split('?')[0];
      ampli.searchLocation({
        search_location_text: locationData.displayName,
        ...(searchContext && { search_context: searchContext }),
      });
      if (searchParams.value.search_context) {
        clearSearchContextFromUrl();
      }
    }
  } catch (error) {
    logger.error('[LocationFinder] Amplitude error:', error);
  }
};

// Computed key for URL params - triggers watch when location changes
const locationKey = computed(() =>
  `${searchParams.value.search}|${searchParams.value.lat}|${searchParams.value.lng}`,
);

// Watch URL params: initial load and changes from header autocomplete
watch(locationKey, () => {
  const urlLocation = getLocationFromUrl();
  if (urlLocation) { fetchSurpriseBags(urlLocation); }
}, { immediate: true });

const handleLoadMore = () => {
  pageToSearch.value++;
  fetchSurpriseBags();
};

const updateFilter = async(filterType: FilterType) => {
  filterBagAvailability.value = filterType;
  isFilterLoading.value = true;

  // Store current data temporarily to prevent layout jump
  tempBags.value = [...surpriseBags.value];

  pageToSearch.value = 0;

  if (locationToSearch.value) {
    await fetchSurpriseBags(locationToSearch.value);
  }

  isFilterLoading.value = false;
  tempBags.value = [];

  try {
    ampli.itemSearchResultsFilter({
      filter: filterType?.toString() || '',
    });
  } catch (error) {
    logger.error('[LocationFinder] Amplitude error:', error);
  }
};

watch(coordinates, (newCoordinates) => {
  if (newCoordinates) {
    fetchSurpriseBags(objectToSearch.value);
  }
});

const event = useRequestEvent();
if (event && props.is404) {
  event.node.res.statusCode = 404;
}

const fetchMapData = async(location: OriginWithPlaceId, options: { radiusKm?: number; applySeq?: number } = {}) => {
  pageToSearch.value = 0;
  await fetchSurpriseBags(location, {
    size: 200,
    replace: true,
    radiusKm: options.radiusKm,
    applySeq: options.applySeq,
  });
};

const handleLoadMapData = async() => {
  if (locationToSearch.value?.center) {
    await fetchMapData(locationToSearch.value);
  }
};

const onMapChanged = (payload: { center: { latitude: number; longitude: number; }; radiusKm: number; zoom?: number }) => {
  if (!payload?.center) {
    return;
  }

  locationToSearch.value = {
    displayName: locationToSearch.value?.displayName || '',
    center: payload.center,
  } as OriginWithPlaceId;

  const seq = ++currentFetchSeq.value;
  fetchMapData(locationToSearch.value, {
    radiusKm: payload.radiusKm,
    applySeq: seq,
  });
};

const handleLocationSelected = (option: OriginWithPlaceId | undefined) => {
  if (option) {
    fetchSurpriseBags(option);
  }
};

</script>

<template>
  <div
    ref="heroComponent"
    class="s-hero"
    data-theme="dark"
  >
    <div class="flex flex-1 items-center">
      <div class="s-hero__text">
        <BaseTitle :pre-title="(!is404 ? preTitle || '' : bagNotFoundCopy?.bagNotFoundPreTitle || '404') as any" :title="(!is404 ? title || '' : bagNotFoundCopy?.bagNotFoundTitle || 'Page not found') as any" class="s-hero__title" tag="h1" />
        <Autocomplete
          :options="suggestions"
          :placeholder="myGeolocation === GeolocationState.ACTIVE ? locationToSearch?.displayName : placeholder"
          :loading="loadingSuggestions"
          :no-location-text="noLocationText"
          :initial-search-value="initialSearchValue"
          @selected="handleLocationSelected"
          @input-change="fetchSuggestions"
        />
        <a
          v-if="!isGeolocationPermissionDenied"
          role="button"
          class="s-hero__geolocation"
          @click="getGeolocation"
        >
          <span>{{ geolocationLabel }}</span>
        </a>
        <div
          v-else
          class="s-hero__geolocation s-hero__geolocation--permission-denied"
          role="status"
          aria-live="polite"
        >
          <span>{{ geolocationLabel }}</span>
        </div>
      </div>
    </div>
    <div class="s-hero__image">
      <BaseImage :image="image" />
    </div>
  </div>
  <SurpriseBagResultsList
    :show-list="showList"
    :empty-results="emptyResults"
    :error-state="errorState"
    :location-to-search="locationToSearch"
    :surprise-bags="surpriseBags"
    :display-bags="displayBags"
    :is-filter-loading="isFilterLoading"
    :filter-bag-availability="filterBagAvailability"
    :disabled-button="disabledButton"
    :results-title="resultsTitle"
    :available-bags-filter-label="availableBagsFilterLabel"
    :empty-state-text="emptyStateText"
    :error-state-text="errorStateText"
    :cta-text="ctaText"
    :static-translations="staticTranslations"
    @update:filter-bag-availability="updateFilter"
    @load-more="handleLoadMore"
    @load-map-data="handleLoadMapData"
    @map-changed="onMapChanged"
  />
</template>

<style scoped>
@import '~/assets/css/mixins/_mixins.index.pcss';

.s-hero {
  display: flex;
  height: auto;
  padding: theme('spacing.120') theme('spacing.0') theme('spacing.72') theme('spacing.0');
  background-color: var(--theme-heading-color-app);
  width: 100%;
  overflow: visible;

  @screen tablet-wide  {
    padding: theme('spacing.96') theme('spacing.0') theme('spacing.0') theme('spacing.0');
  }

  &__title {
    display: flex;
    justify-content: flex-start;
    text-align: left;
    padding-bottom: theme('spacing.20');
    flex-direction: column;
  }

  &__text {
    display: flex;
    flex: 1;
    flex-direction: column;
    width: auto;
    padding: theme('spacing.0') theme('spacing.32');

    @screen desk {
      padding: theme('spacing.0') theme('spacing.96');
    }

    @media (min-width: 1600px) {
      padding-left: 25%;
    }

    :deep(.c-base-title__pre-title) {
      text-align: left;
    }

    :deep(.c-base-title__header, .c-base-title--left) {
      text-align: left;
      color: var(--theme-background-color-app) !important;
    }
  }

  &__image {
    --img-max-height: 374px;

    display: none;

    @screen tablet-wide {
      display: flex;
      flex: 1;
      justify-content: flex-end;
      height: var(--img-max-height);
    }

    @screen desk {
      max-height: var(--img-max-height);
    }
  }

  &__geolocation {
    color: var(--theme-background-color-app);
    margin-top: theme('spacing.12');
    display: flex;
    align-items: flex-start;
    gap: theme('spacing.8');
    min-height: 5rem;

    &:before {
      content: '';
      width: 24px;
      height: 24px;
      min-width: 24px;
      flex-shrink: 0;
      display: block;
      background-repeat: no-repeat;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none' stroke='white' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 21c-4.8-4.8-8-8.1-8-11.5a8 8 0 0 1 16 0c0 3.4-3.2 6.7-8 11.5z'/%3E%3Ccircle cx='12' cy='9' r='2.5'/%3E%3C/svg%3E");
    }
    &:hover {
      cursor: pointer;
      text-decoration-color: var(--btn-primary-hover-type);
      color: var(--btn-primary-hover-type);
    }

    &--permission-denied {
      cursor: default;
      pointer-events: none;
      user-select: text;

      &:hover {
        color: var(--theme-background-color-app);
        text-decoration: none;
      }
    }
  }
}

</style>
